import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { PageResponse } from './types'

// ------------------------------------------------------------------- types

export type ReportStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'REVISED'

export type Shift = 'DAY' | 'NIGHT' | 'GENERAL'

export type Weather =
  | 'CLEAR'
  | 'CLOUDY'
  | 'RAIN'
  | 'HEAVY_RAIN'
  | 'STORM'
  | 'EXTREME_HEAT'
  | 'FOG'

export type WorkItemStatus = 'PLANNED' | 'PROCESSING' | 'COMPLETED' | 'PENDING'
export type QualityStatus = 'ACCEPTED' | 'PARTIALLY_ACCEPTED' | 'REJECTED' | 'UNDER_INSPECTION'
export type IssuePriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type IssueStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'DEFERRED'

export interface DailyReportSummary {
  id: string
  reportNumber: string
  reportDate: string
  shift: Shift
  siteId: string
  siteCode?: string
  siteName?: string
  projectId: string
  projectName?: string
  status: ReportStatus
  weather?: Weather
  overallProgressPct?: number
  siteEngineerName?: string
  submittedAt?: string
  approvedAt?: string
  editable: boolean
}

export interface DailyReport extends DailyReportSummary {
  siteEngineerId?: string
  temperatureC?: number
  rainHours?: number
  todayPlannedWork?: string
  todayActualWork?: string
  generalRemarks?: string
  revisionNo: number
  reviewedAt?: string
  rejectedAt?: string
  rejectionReason?: string
  geofenceOk?: boolean
  clientVisible: boolean
  counts: {
    materialReceipts: number
    labourEntries: number
    workProgress: number
    issues: number
  }
  version: number
}

export interface WorkItemPrefill {
  workItemId: string
  code: string
  description: string
  workPackageName?: string
  uomId: string
  uomCode?: string
  totalPlannedQuantity: number
  previousCompletedQuantity: number
  balanceQuantity: number
  overallProgressPct?: number
  status: WorkItemStatus
  alreadyRecordedToday: boolean
}

export interface WorkProgress {
  id: string
  /** Null for work that is not on the bill; such a line is identified by its description. */
  workItemId?: string | null
  workItemCode?: string
  description?: string
  workPackageName?: string
  uomId: string
  uomCode?: string
  siteLocationName?: string
  /** Null when there is no BOQ line, so no percentage or balance is produced either. */
  totalPlannedQuantity?: number | null
  previousCompletedQuantity: number
  todayPlannedQuantity: number
  todayCompletedQuantity: number
  cumulativeCompletedQuantity?: number
  balanceQuantity?: number
  todayProgressPct?: number
  overallProgressPct?: number
  status: WorkItemStatus
  delayReason?: string
  remarks?: string
  overridden: boolean
  version: number
}

/** Whose workers these were. Derived by the database from the contractor. */
export type LabourScope = 'OWN' | 'CONTRACTOR'

/** Whose material this was: our procurement, contractor supply, or client issue. */
export type SupplyScope = 'OWN' | 'CONTRACTOR' | 'CLIENT'

export interface LabourEntry {
  id: string
  contractorId?: string
  contractorName?: string
  labourScope: LabourScope
  labourCategoryId: string
  labourCategoryName?: string
  trade?: string
  /** Headcount expected, from a template or the day plan. Null when none was set. */
  plannedWorkers?: number
  maleCount: number
  femaleCount: number
  skilledCount: number
  semiSkilledCount: number
  unskilledCount: number
  totalWorkers?: number
  workingHours: number
  overtimeHours: number
  manHours?: number
  siteLocationName?: string
  workDescription?: string
  remarks?: string
  version: number
}

export interface MaterialReceiptItem {
  id: string
  materialId: string
  materialCode?: string
  materialName?: string
  uomCode?: string
  orderedQuantity: number
  receivedQuantity: number
  acceptedQuantity: number
  rejectedQuantity: number
  balanceQuantity?: number
  rate?: number
  amount?: number
  qualityStatus: QualityStatus
  remarks?: string
}

export interface MaterialReceipt {
  id: string
  receiptNumber?: string
  supplierId?: string
  supplierName?: string
  supplyScope: SupplyScope
  contractorId?: string
  contractorName?: string
  poNumber?: string
  challanNumber?: string
  challanDate?: string
  vehicleNumber?: string
  receivedTime?: string
  qualityStatus: QualityStatus
  inspectionStatus: string
  remarks?: string
  totalAmount?: number
  items: MaterialReceiptItem[]
  version: number
}

export interface SiteIssue {
  id: string
  issueNumber?: string
  issueDate: string
  issueCategoryId?: string
  categoryName?: string
  description: string
  impact?: string
  priority: IssuePriority
  responsibleParty?: string
  targetResolutionDate?: string
  actualResolutionDate?: string
  status: IssueStatus
  ageDays: number
  overdue: boolean
  version: number
}

export interface Completeness {
  id: string
  score: number
  sections: { key: string; label: string; complete: boolean; count: number; hint?: string }[]
}

export interface SubmitResult {
  id: string
  status: ReportStatus
  geofenceOk?: boolean
  warnings: { code: string; message: string }[]
}

export interface HistoryEntry {
  id: string
  fromStatus?: ReportStatus
  toStatus: ReportStatus
  action: string
  actorName?: string
  remarks?: string
  createdAt: string
}

// ------------------------------------------------------------------- hooks

export interface ReportListParams {
  projectId?: string
  siteId?: string
  status?: ReportStatus | ''
  pendingApproval?: boolean
  dateFrom?: string
  dateTo?: string
  page: number
  size: number
}

function clean(params: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  )
}

export function useDailyReports(params: ReportListParams) {
  return useQuery({
    queryKey: ['daily-reports', params],
    queryFn: async () => {
      const { data } = await api.get<PageResponse<DailyReportSummary>>('/daily-reports', {
        params: clean(params),
      })
      return data
    },
  })
}

export function useDailyReport(id?: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['daily-report', id],
    queryFn: async () => {
      const { data } = await api.get<DailyReport>(`/daily-reports/${id}`)
      return data
    },
  })
}

export function useCreateDailyReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      siteId: string
      reportDate: string
      shift?: Shift
      weather?: Weather | ''
      todayPlannedWork?: string
    }) => {
      const { data } = await api.post<DailyReport>('/daily-reports', clean(body))
      return data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['daily-reports'] }),
  })
}

export function useUpdateDailyReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.put<DailyReport>(`/daily-reports/${id}`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['daily-report', id] })
      void qc.invalidateQueries({ queryKey: ['daily-reports'] })
    },
  })
}

/** submit | review | approve | reject | reopen */
export function useReportAction(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ action, body }: { action: string; body?: Record<string, unknown> }) => {
      const { data } = await api.post(`/daily-reports/${id}/${action}`, body ?? {})
      return data as SubmitResult
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['daily-report', id] })
      void qc.invalidateQueries({ queryKey: ['daily-reports'] })
      void qc.invalidateQueries({ queryKey: ['completeness', id] })
    },
  })
}

export function useCompleteness(id?: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['completeness', id],
    queryFn: async () => {
      const { data } = await api.get<Completeness>(`/daily-reports/${id}/completeness`)
      return data
    },
  })
}

export function useReportHistory(id?: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['report-history', id],
    queryFn: async () => {
      const { data } = await api.get<HistoryEntry[]>(`/daily-reports/${id}/history`)
      return data
    },
  })
}

/** One hook shape for every child collection; they all hang off a report id. */
function childCollection<T>(path: string, key: string) {
  return {
    useList(reportId?: string) {
      return useQuery({
        enabled: Boolean(reportId),
        queryKey: [key, reportId],
        queryFn: async () => {
          const { data } = await api.get<T[]>(path, { params: { dailyReportId: reportId } })
          return data
        },
      })
    },
    useCreate(reportId: string) {
      const qc = useQueryClient()
      return useMutation({
        mutationFn: async (body: Record<string, unknown>) => {
          const { data } = await api.post<T>(path, { ...body, dailyReportId: reportId })
          return data
        },
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: [key, reportId] })
          void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
          void qc.invalidateQueries({ queryKey: ['completeness', reportId] })
        },
      })
    },
    useUpdate(reportId: string) {
      const qc = useQueryClient()
      return useMutation({
        mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
          const { data } = await api.put<T>(`${path}/${id}`, { ...body, dailyReportId: reportId })
          return data
        },
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: [key, reportId] })
          void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
          void qc.invalidateQueries({ queryKey: ['completeness', reportId] })
        },
      })
    },
    useDelete(reportId: string) {
      const qc = useQueryClient()
      return useMutation({
        mutationFn: async (id: string) => {
          await api.delete(`${path}/${id}`)
        },
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: [key, reportId] })
          void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
          void qc.invalidateQueries({ queryKey: ['completeness', reportId] })
        },
      })
    },
  }
}

export const workProgressApi = childCollection<WorkProgress>('/work-progress', 'work-progress')
export const labourApi = childCollection<LabourEntry>('/labour-entries', 'labour-entries')
export const materialReceiptApi = childCollection<MaterialReceipt>(
  '/material-receipts',
  'material-receipts',
)
export const siteIssueApi = childCollection<SiteIssue>('/site-issues', 'site-issues')

export function useWorkItemPrefill(reportId?: string) {
  return useQuery({
    enabled: Boolean(reportId),
    queryKey: ['work-prefill', reportId],
    queryFn: async () => {
      const { data } = await api.get<WorkItemPrefill[]>('/work-progress/prefill', {
        params: { dailyReportId: reportId },
      })
      return data
    },
  })
}

// --------------------------------------------------- labour quick entry

export interface LabourQuickRow {
  labourCategoryId: string
  contractorId?: string
  count: number
  femaleCount?: number
  workingHours?: number
  overtimeHours?: number
  workDescription?: string
}

export function useLabourBulk(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rows: LabourQuickRow[]) => {
      const { data } = await api.post<LabourEntry[]>('/labour-entries/bulk', {
        dailyReportId: reportId,
        rows,
      })
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labour-entries', reportId] })
      void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
      void qc.invalidateQueries({ queryKey: ['completeness', reportId] })
    },
  })
}

// ------------------------------------------------------------- drawings

export interface DrawingReceived {
  revisionId: string
  drawingId: string
  drawingNumber?: string
  title?: string
  disciplineName?: string
  consultantName?: string
  revisionNumber: string
  revisionDate?: string
  receivedDate: string
  receivedFrom?: string
  attachmentId?: string
  remarks?: string
}

export function useDrawingsForReport(reportId?: string) {
  return useQuery({
    enabled: Boolean(reportId),
    queryKey: ['drawings-received', reportId],
    queryFn: async () => {
      const { data } = await api.get<DrawingReceived[]>('/drawings/by-report', {
        params: { dailyReportId: reportId },
      })
      return data
    },
  })
}

/** Multipart, because the drawing file usually arrives with the drawing. */
export function useRecordDrawing(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ data, file }: { data: Record<string, unknown>; file?: File | null }) => {
      const form = new FormData()
      form.append(
        'data',
        new Blob([JSON.stringify({ ...data, dailyReportId: reportId })], {
          type: 'application/json',
        }),
      )
      if (file) form.append('file', file)
      const { data: result } = await api.post('/drawings/received', form)
      return result
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drawings-received', reportId] })
      void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
    },
  })
}

/** Corrects what was recorded when a revision was received - usually a date or a number. */
export function useUpdateDrawingRevision(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ revisionId, body }: { revisionId: string; body: Record<string, unknown> }) => {
      const { data } = await api.put(`/drawings/revisions/${revisionId}`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drawings-received', reportId] })
      void qc.invalidateQueries({ queryKey: ['drawings', 'pending'] })
      void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
    },
  })
}

/** Removes a drawing logged by mistake; the register entry itself stays. */
export function useDeleteDrawingRevision(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (revisionId: string) => {
      await api.delete(`/drawings/revisions/${revisionId}`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drawings-received', reportId] })
      void qc.invalidateQueries({ queryKey: ['drawings', 'pending'] })
      void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
    },
  })
}

/** Edits a register entry: what the drawing is, who owes it, when it is due. */
export function useUpdateDrawing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      const { data } = await api.put(`/drawings/${id}`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drawings'] })
      void qc.invalidateQueries({ queryKey: ['drawings-received'] })
    },
  })
}

export function useDeleteDrawing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/drawings/${id}`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drawings'] })
      void qc.invalidateQueries({ queryKey: ['drawings-received'] })
    },
  })
}

export function useDisciplines() {
  return useQuery({
    queryKey: ['disciplines'],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; code: string; name: string }[]>(
        '/masters/disciplines',
      )
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useDrawingTypes() {
  return useQuery({
    queryKey: ['drawing-types'],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; code: string; name: string }[]>(
        '/masters/drawing-types',
      )
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useConsultants() {
  return useQuery({
    queryKey: ['consultants'],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; code: string; name: string; extra?: string }[]>(
        '/parties/consultants',
      )
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

// ------------------------------------------------------------- planning

export type PlanPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type WorkPlanStatus =
  | 'PLANNED'
  | 'READY'
  | 'MATERIAL_PENDING'
  | 'LABOUR_PENDING'
  | 'DRAWING_PENDING'
  | 'STARTED'
  | 'COMPLETED'
  | 'POSTPONED'

export interface MaterialPlan {
  id: string
  planningDate: string
  materialId: string
  materialCode?: string
  materialName?: string
  uomCode?: string
  requiredQuantity: number
  availableQuantity: number
  shortageQuantity?: number
  requiredByTime?: string
  supplierName?: string
  priority: PlanPriority
  status: string
  remarks?: string
}

export interface LabourPlan {
  id: string
  planningDate: string
  labourCategoryId: string
  labourCategoryName?: string
  contractorId?: string
  contractorName?: string
  requiredWorkers: number
  availableWorkers: number
  shortageWorkers?: number
  workingHours: number
  remarks?: string
}

export interface WorkPlan {
  id: string
  planningDate: string
  workPackageName?: string
  workItemCode?: string
  description: string
  plannedQuantity?: number
  uomCode?: string
  requiredLabourCount?: number
  plannedStartTime?: string
  plannedEndTime?: string
  priority: PlanPriority
  status: WorkPlanStatus
  blocked: boolean
  carriedForwardFromId?: string
  remarks?: string
}

export interface PlanSummary {
  planningDate: string
  siteId: string
  materials: MaterialPlan[]
  labour: LabourPlan[]
  work: WorkPlan[]
  alerts: {
    materialShortages: number
    labourShortages: number
    blockedActivities: number
    criticalItems: number
  }
}

export function usePlanSummary(reportId?: string) {
  return useQuery({
    enabled: Boolean(reportId),
    queryKey: ['plan-summary', reportId],
    queryFn: async () => {
      const { data } = await api.get<PlanSummary>('/planning/summary', {
        params: { dailyReportId: reportId },
      })
      return data
    },
  })
}

function planMutation<T>(path: string, reportId: string) {
  return {
    create: async (body: Record<string, unknown>) => {
      const { data } = await api.post<T>(path, { ...body, dailyReportId: reportId })
      return data
    },
    update: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      const { data } = await api.put<T>(`${path}/${id}`, { ...body, dailyReportId: reportId })
      return data
    },
    remove: async (id: string) => {
      await api.delete(`${path}/${id}`)
    },
  }
}

export function usePlanMutations(reportId: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['plan-summary', reportId] })
  }
  const material = planMutation<MaterialPlan>('/planning/material', reportId)
  const labour = planMutation<LabourPlan>('/planning/labour', reportId)
  const work = planMutation<WorkPlan>('/planning/work', reportId)

  return {
    addMaterial: useMutation({ mutationFn: material.create, onSuccess: invalidate }),
    addLabour: useMutation({ mutationFn: labour.create, onSuccess: invalidate }),
    addWork: useMutation({ mutationFn: work.create, onSuccess: invalidate }),
    editMaterial: useMutation({ mutationFn: material.update, onSuccess: invalidate }),
    editLabour: useMutation({ mutationFn: labour.update, onSuccess: invalidate }),
    editWork: useMutation({ mutationFn: work.update, onSuccess: invalidate }),
    removeMaterial: useMutation({ mutationFn: material.remove, onSuccess: invalidate }),
    removeLabour: useMutation({ mutationFn: labour.remove, onSuccess: invalidate }),
    removeWork: useMutation({ mutationFn: work.remove, onSuccess: invalidate }),
    carryForward: useMutation({
      mutationFn: async () => {
        const { data } = await api.post('/planning/work/carry-forward', null, {
          params: { dailyReportId: reportId },
        })
        return data as { carried: number }
      },
      onSuccess: invalidate,
    }),
  }
}

// ------------------------------------------------- drawing follow-up

export interface DrawingFollowupDto {
  id: string
  drawingId: string
  followupDate: string
  followupByName?: string
  followupWith?: string
  communicationMode: string
  remarks?: string
  expectedDate?: string
  nextFollowupDate?: string
  responseReceived: boolean
  status: string
}

export interface PendingDrawing {
  drawingId: string
  drawingNumber: string
  title: string
  currentRevision?: string
  projectId: string
  siteId?: string
  consultantId?: string
  disciplineId?: string
  drawingTypeId?: string
  consultantName?: string
  responsibleParty?: string
  requestedDate?: string
  daysPending?: number
  expectedDate?: string
  lastFollowupDate?: string
  nextFollowupDate?: string
  followupCount: number
  status: string
  overdue: boolean
  overdueByDays?: number
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  remarks?: string
}

export function usePendingDrawings(params: { projectId?: string; siteId?: string; page: number; size: number }) {
  return useQuery({
    queryKey: ['pending-drawings', params],
    queryFn: async () => {
      const { data } = await api.get<PageResponse<PendingDrawing>>('/drawings/pending', {
        params: clean(params),
      })
      return data
    },
  })
}

export function useDrawingFollowups(drawingId?: string) {
  return useQuery({
    enabled: Boolean(drawingId),
    queryKey: ['drawing-followups', drawingId],
    queryFn: async () => {
      const { data } = await api.get<DrawingFollowupDto[]>(`/drawings/${drawingId}/followups`)
      return data
    },
  })
}

export function useAddFollowup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ drawingId, body }: { drawingId: string; body: Record<string, unknown> }) => {
      const { data } = await api.post<DrawingFollowupDto>(`/drawings/${drawingId}/followups`, body)
      return data
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['drawing-followups', vars.drawingId] })
      void qc.invalidateQueries({ queryKey: ['pending-drawings'] })
    },
  })
}

export function useRegisterExpectedDrawing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.post('/drawings/expected', body)
      return data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pending-drawings'] }),
  })
}

// --------------------------------------------------------------- photos

export interface SitePhotoDto {
  id: string
  attachmentId: string
  workProgressId?: string
  description?: string
  capturedAt?: string
  serverReceivedAt: string
  latitude?: number
  longitude?: number
  deviceModel?: string
  fileName: string
  sizeBytes: number
  contentType: string
  url: string
}

export function useSitePhotos(reportId?: string) {
  return useQuery({
    enabled: Boolean(reportId),
    queryKey: ['site-photos', reportId],
    queryFn: async () => {
      const { data } = await api.get<SitePhotoDto[]>('/site-photos', {
        params: { dailyReportId: reportId },
      })
      return data
    },
  })
}

export function useUploadPhoto(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      file,
      description,
      workProgressId,
    }: {
      file: File
      description?: string
      workProgressId?: string
    }) => {
      const form = new FormData()
      form.append('file', file)
      const params = new URLSearchParams({ dailyReportId: reportId })
      if (description) params.set('description', description)
      if (workProgressId) params.set('workProgressId', workProgressId)
      const { data } = await api.post<SitePhotoDto>(`/site-photos?${params.toString()}`, form)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['site-photos', reportId] })
      void qc.invalidateQueries({ queryKey: ['daily-report', reportId] })
    },
  })
}

export function useDeletePhoto(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/site-photos/${id}`)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['site-photos', reportId] }),
  })
}

export function useIssueCategories() {
  return useQuery({
    queryKey: ['issue-categories'],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; code: string; name: string }[]>(
        '/masters/issue-categories',
      )
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useContractors() {
  return useQuery({
    queryKey: ['contractors'],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; code: string; name: string }[]>(
        '/parties/contractors',
      )
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; code: string; name: string }[]>(
        '/parties/suppliers',
      )
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}
